import React, { useMemo } from 'react';
import {
  CaretLeftIcon,
  CaretRightIcon,
  CaretDoubleLeftIcon,
  CaretDoubleRightIcon,
} from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  SelectDropdown,
  SelectDropdownOption,
} from '@/components/common/SelectDropdown';

export interface PaginationState {
  pageSize: number;
  currentPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  totalItems?: number;
  startItem?: number;
  endItem?: number;
}

export interface TablePaginationProps {
  pagination: PaginationState;
  onPageSizeChange: (pageSize: number) => void;
  onPreviousPage: () => void;
  onNextPage: () => void;
  onFirstPage?: () => void;
  onLastPage?: () => void;
  onGoToPage?: (page: number) => void;
  showRowsPerPage?: boolean;
  showPageInfo?: boolean;
  showPageNumbers?: boolean;
  rowsPerPageOptions?: number[];
}

/**
 * Reusable pagination component using shadcn/ui components.
 * Works with both cursor-based and offset-based pagination.
 */
export const TablePagination: React.FC<TablePaginationProps> = ({
  pagination,
  onPageSizeChange,
  onPreviousPage,
  onNextPage,
  onFirstPage,
  onLastPage,
  onGoToPage,
  showRowsPerPage = true,
  showPageInfo = true,
  showPageNumbers = false,
  rowsPerPageOptions = [10, 20, 25, 30, 40, 50, 100],
}) => {
  const {
    pageSize,
    currentPage,
    hasNextPage,
    hasPreviousPage,
    totalItems,
    startItem,
    endItem,
  } = pagination;

  // Generate page options for dropdown when we have totalItems and onGoToPage
  const pageOptions = useMemo<SelectDropdownOption[]>(() => {
    if (totalItems === undefined || !onGoToPage) {
      return [];
    }
    const totalPages = Math.ceil(totalItems / pageSize);
    return Array.from({ length: totalPages }, (_, i) => {
      const page = i + 1;
      return {
        id: `page-${page}`,
        label: `${page}`, // Just the page number in dropdown
        display: `Page ${page} of ${totalPages}`, // Full text when collapsed
        value: page,
      };
    });
  }, [totalItems, pageSize, onGoToPage]);

  // Current page option for SelectDropdown
  const currentPageOption = useMemo<SelectDropdownOption | null>(() => {
    if (totalItems === undefined || !onGoToPage) {
      return null;
    }
    return pageOptions.find((opt) => opt.value === currentPage) || null;
  }, [pageOptions, currentPage, totalItems, onGoToPage]);

  return (
    <div className="flex items-center justify-between px-2 py-4">
      {/* Left side - Item count info */}
      <div className="flex-1 text-sm text-muted-foreground">
        {showPageInfo &&
          totalItems !== undefined &&
          startItem !== undefined &&
          endItem !== undefined && (
            <>
              Showing {startItem} to {endItem} of {totalItems} results
            </>
          )}
        {showPageInfo && totalItems === undefined && (
          <>
            Page {currentPage}
            {hasNextPage && ' of many'}
          </>
        )}
      </div>

      {/* Right side - Controls */}
      <div className="flex items-center space-x-6 lg:space-x-8">
        {/* Rows per page selector */}
        {showRowsPerPage && (
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium">Rows per page</p>
            <Select
              value={`${pageSize}`}
              onValueChange={(value) => onPageSizeChange(Number(value))}
            >
              <SelectTrigger className="h-8 w-[80px]">
                <SelectValue placeholder={pageSize} />
              </SelectTrigger>
              <SelectContent side="top">
                {rowsPerPageOptions.map((size) => (
                  <SelectItem key={size} value={`${size}`}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Page info */}
        {showPageInfo && (
          <>
            {onGoToPage &&
            totalItems !== undefined &&
            pageOptions.length > 0 ? (
              <SelectDropdown
                options={pageOptions}
                value={currentPageOption}
                onChange={(selected) => {
                  if (selected?.value && typeof selected.value === 'number') {
                    onGoToPage(selected.value);
                  }
                }}
                placeholder="Select page"
                fullWidth={false}
                size="default"
                className="w-[140px]"
              />
            ) : (
              <div className="flex w-[100px] items-center justify-center text-sm font-medium">
                {totalItems !== undefined ? (
                  <>
                    Page {currentPage} of {Math.ceil(totalItems / pageSize)}
                  </>
                ) : (
                  <>Page {currentPage}</>
                )}
              </div>
            )}
          </>
        )}

        {/* Navigation buttons */}
        <div className="flex items-center space-x-2">
          {/* First page button */}
          {onFirstPage && (
            <Button
              variant="outline"
              size="icon"
              className="hidden size-8 lg:flex"
              onClick={onFirstPage}
              disabled={!hasPreviousPage}
            >
              <span className="sr-only">Go to first page</span>
              <CaretDoubleLeftIcon className="h-4 w-4" />
            </Button>
          )}

          {/* Previous page button */}
          <Button
            variant="outline"
            size="icon"
            className="size-8"
            onClick={onPreviousPage}
            disabled={!hasPreviousPage}
          >
            <span className="sr-only">Go to previous page</span>
            <CaretLeftIcon className="h-4 w-4" />
          </Button>

          {/* Page number buttons */}
          {showPageNumbers &&
            onGoToPage &&
            totalItems !== undefined &&
            (() => {
              const totalPages = Math.ceil(totalItems / pageSize);
              const maxVisiblePages = 5;
              let startPage = Math.max(
                1,
                currentPage - Math.floor(maxVisiblePages / 2),
              );
              const endPage = Math.min(
                totalPages,
                startPage + maxVisiblePages - 1,
              );

              // Adjust start if we're near the end
              if (endPage - startPage + 1 < maxVisiblePages) {
                startPage = Math.max(1, endPage - maxVisiblePages + 1);
              }

              const pages = [];
              for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
              }

              return (
                <div className="flex items-center space-x-1">
                  {startPage > 1 && (
                    <>
                      <Button
                        variant={1 === currentPage ? 'default' : 'outline'}
                        size="icon"
                        className="size-8 text-xs"
                        onClick={() => onGoToPage(1)}
                      >
                        1
                      </Button>
                      {startPage > 2 && (
                        <span className="text-muted-foreground">...</span>
                      )}
                    </>
                  )}
                  {pages.map((page) => (
                    <Button
                      key={page}
                      variant={page === currentPage ? 'default' : 'outline'}
                      size="icon"
                      className="size-8 text-xs"
                      onClick={() => onGoToPage(page)}
                    >
                      {page}
                    </Button>
                  ))}
                  {endPage < totalPages && (
                    <>
                      {endPage < totalPages - 1 && (
                        <span className="text-muted-foreground">...</span>
                      )}
                      <Button
                        variant={
                          totalPages === currentPage ? 'default' : 'outline'
                        }
                        size="icon"
                        className="size-8 text-xs"
                        onClick={() => onGoToPage(totalPages)}
                      >
                        {totalPages}
                      </Button>
                    </>
                  )}
                </div>
              );
            })()}

          {/* Next page button */}
          <Button
            variant="outline"
            size="icon"
            className="size-8"
            onClick={onNextPage}
            disabled={!hasNextPage}
          >
            <span className="sr-only">Go to next page</span>
            <CaretRightIcon className="h-4 w-4" />
          </Button>

          {/* Last page button */}
          {onLastPage && (
            <Button
              variant="outline"
              size="icon"
              className="hidden size-8 lg:flex"
              onClick={onLastPage}
              disabled={!hasNextPage}
            >
              <span className="sr-only">Go to last page</span>
              <CaretDoubleRightIcon className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
